// ============================================
// components/salon/SalonLayout.js
// Wraps all salon pages with SalonSidebar + header
// ============================================
import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import SalonSidebar from './SalonSidebar';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';

export default function SalonLayout({ children, hideHeader = false, noPadding = false }) {
  const { userData, userToken: token } = useAuth();
  const [salonMeta, setSalonMeta] = useState({ salonName: '', contactPersonName: '', contactPhone: '', logoUrl: '' });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem('salonSidebarCollapsed') === 'true'
  );

  // Stay in sync if sidebar collapse state changes
  useEffect(() => {
    const onStorage = () =>
      setIsCollapsed(localStorage.getItem('salonSidebarCollapsed') === 'true');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchMeta = async () => {
      if (!token) return;
      try {
        const res = await fetch(SALON_API_URL + '/admin-settings', {
          headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        if (!cancelled && data.success) {
          const profile = data.data?.salonProfile || {};
          setSalonMeta({
            salonName: profile.salonName || '',
            contactPersonName: profile.contactPersonName || '',
            contactPhone: profile.contactPhone || '',
            logoUrl: profile.logoUrl || '',
          });
        }
      } catch {}
    };
    fetchMeta();
    return () => { cancelled = true; };
  }, [token]);

  const displaySalonName = salonMeta.salonName || userData?.salonName || 'My Salon';
  const displayContactName = salonMeta.contactPersonName || userData?.contactPersonName || 'Salon Admin';

  return (
    <div className={`bg-slate-50 dark:bg-slate-900 flex text-slate-900 dark:text-slate-100 ${noPadding ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      {/* Sidebar */}
      <SalonSidebar
        isMobileOpen={mobileOpen}
        setIsMobileOpen={setMobileOpen}
        onCollapseChange={setIsCollapsed}
      />

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isCollapsed ? 'lg:ml-14' : 'lg:ml-64'} ${noPadding ? 'h-screen overflow-hidden' : ''}`}>
        {!hideHeader && (
          <header className="sticky top-0 z-30 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-4 lg:px-8 py-3.5 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Menu size={20} />
              </button>
              <h2 className="font-black text-lg lg:text-xl text-slate-900 dark:text-white tracking-tight">
                {displaySalonName}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-xs font-bold text-slate-900 dark:text-white">{displayContactName}</p>
                {salonMeta.contactPhone && (
                  <p className="text-[10px] text-slate-400 font-medium">{salonMeta.contactPhone}</p>
                )}
              </div>
              <div className="w-9 h-9 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-sm shadow-xs">
                {displaySalonName.charAt(0).toUpperCase()}
              </div>
            </div>
          </header>
        )}

        <main className={`flex-1 overflow-x-hidden ${noPadding ? 'overflow-y-hidden' : 'p-4 lg:p-8'}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
